import React from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

export interface StatItem {
    id?: string;
    label: string;
    value: string | number;
    icon?: LucideIcon | React.ReactNode;
    color?: 'default' | 'yellow' | 'orange' | 'green' | 'blue' | 'red' | 'gray' | 'purple';
}

interface HorizontalStatsStripProps {
    stats: StatItem[];
    className?: string;
    /** Number of columns to show on desktop. Defaults to 4 */
    columns?: 3 | 4 | 5 | 6;
}

/**
 * HorizontalStatsStrip - A mobile-optimized horizontal scrollable stats strip.
 * 
 * Designed to replace vertical stacked stat grids on mobile.
 * Shows compact stat items in a horizontally scrollable container.
 * On desktop (md+), switches to a standard grid layout.
 */
export const HorizontalStatsStrip: React.FC<HorizontalStatsStripProps> = ({
    stats,
    className,
    columns = 4,
}) => {
    const colorStyles = {
        default: 'bg-gray-50 border-gray-200 text-gray-700',
        yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
        orange: 'bg-orange-50 border-orange-200 text-orange-700',
        green: 'bg-green-50 border-green-200 text-green-700',
        blue: 'bg-blue-50 border-blue-200 text-blue-700',
        red: 'bg-red-50 border-red-200 text-red-700',
        gray: 'bg-gray-50 border-gray-200 text-gray-700',
        purple: 'bg-purple-50 border-purple-200 text-purple-700',
    };

    const iconColorStyles = {
        default: 'text-gray-500',
        yellow: 'text-yellow-600',
        orange: 'text-orange-600',
        green: 'text-green-600',
        blue: 'text-blue-600',
        red: 'text-red-600',
        gray: 'text-gray-600',
        purple: 'text-purple-600',
    };

    const columnsClass = {
        3: 'md:grid-cols-3',
        4: 'md:grid-cols-4',
        5: 'md:grid-cols-5',
        6: 'md:grid-cols-6',
    };

    return (
        <div className={cn("relative", className)}>
            {/* Mobile: Horizontal scroll */}
            <div className="md:hidden">
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                    {stats.map((stat, index) => {
                        const color = stat.color || 'default';
                        const renderIcon = () => {
                            if (!stat.icon) return null;
                            if (React.isValidElement(stat.icon)) return stat.icon;
                            const IconComponent = stat.icon as LucideIcon;
                            return <IconComponent className={cn("h-3.5 w-3.5", iconColorStyles[color])} />;
                        };
                        return (
                            <div
                                key={stat.id || `stat-${index}`}
                                className={cn(
                                    "flex-shrink-0 min-w-[100px] rounded-lg border p-2.5 text-center",
                                    colorStyles[color]
                                )}
                            >
                                <div className="flex items-center justify-center gap-1 mb-0.5">
                                    {renderIcon()}
                                    <span className="text-[10px] font-medium uppercase tracking-wide opacity-80">
                                        {stat.label}
                                    </span>
                                </div>
                                <div className="text-lg font-bold">{stat.value}</div>
                            </div>
                        );
                    })}
                </div>
                {/* Scroll indicator gradient */}
                <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none md:hidden" />
            </div>

            {/* Desktop: Grid layout */}
            <div className={cn("hidden md:grid gap-4", columnsClass[columns])}>
                {stats.map((stat, index) => {
                    const color = stat.color || 'default';
                    const renderIcon = () => {
                        if (!stat.icon) return null;
                        if (React.isValidElement(stat.icon)) return stat.icon;
                        const IconComponent = stat.icon as LucideIcon;
                        return <IconComponent className={cn("h-4 w-4", iconColorStyles[color])} />;
                    };
                    return (
                        <div
                            key={stat.id || `stat-${index}`}
                            className={cn(
                                "rounded-lg border p-4 text-center shadow-sm hover:shadow-md transition-shadow",
                                colorStyles[color]
                            )}
                        >
                            <div className="flex items-center justify-center gap-1 mb-1">
                                {renderIcon()}
                                <span className="text-sm font-medium">{stat.label}</span>
                            </div>
                            <div className="text-2xl font-bold">{stat.value}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default HorizontalStatsStrip;
